import React, { useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import { Empty, Typography } from "antd";
import "leaflet/dist/leaflet.css";
import { HEALTH_DOT_COLOR, HEALTH_STATE_LABEL } from "../constants/health";

const { Text } = Typography;

// Centre of Pakistan — the fallback view when nothing is geolocated yet.
const DEFAULT_CENTER = [30.3753, 69.3451];
const DEFAULT_ZOOM = 5;

// Keep the map focused on Pakistan: markers only ever sit here, so lock panning to
// the country's bounding box and stop zooming out past the whole country.
const PAKISTAN_BOUNDS = [
  [23.5, 60.8], // south-west
  [37.2, 77.9], // north-east
];
const MIN_ZOOM = 5;

// Fit the map to the plotted markers once, whenever the set of points changes.
function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    map.fitBounds(points, { padding: [40, 40] });
  }, [map, points]);
  return null;
}

// Fleet map: one dot per device whose outlet has captured GPS, coloured by health.
// Devices with no outlet coordinates simply don't appear (the feed carries no geo;
// only in-field GeoCapture sets it), so the count is surfaced by the caller.
export default function FleetMap({ devices = [], height = 420 }) {
  const geoDevices = useMemo(
    () => devices.filter((d) => d.outlet?.lat != null && d.outlet?.lng != null),
    [devices]
  );
  const points = useMemo(
    () => geoDevices.map((d) => [d.outlet.lat, d.outlet.lng]),
    [geoDevices]
  );

  if (!geoDevices.length) {
    return (
      <Empty
        description={
          <Text type="secondary">
            No outlet has GPS coordinates yet — locations are captured in the field
            during device onboarding, not from the outlet sync.
          </Text>
        }
      />
    );
  }

  return (
    <div style={{ height, width: "100%", borderRadius: 8, overflow: "hidden" }}>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        minZoom={MIN_ZOOM}
        maxBounds={PAKISTAN_BOUNDS}
        maxBoundsViscosity={1.0}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {geoDevices.map((d) => {
          const color = HEALTH_DOT_COLOR[d.color] || "#8c8c8c";
          const o = d.outlet;
          return (
            <CircleMarker
              key={d.deviceId}
              center={[o.lat, o.lng]}
              radius={7}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.85, weight: 1 }}
            >
              <Popup>
                <div style={{ lineHeight: 1.5 }}>
                  <strong>{d.deviceTitle || d.deviceId}</strong>
                  <br />
                  {HEALTH_STATE_LABEL[d.state] || d.state}
                  <br />
                  {o.title}
                  {o.code ? ` (${o.code})` : ""}
                  <br />
                  {[o.region, o.zone].filter(Boolean).join(" › ")}
                  {o.supervisor ? (
                    <>
                      <br />
                      Supervisor: {o.supervisor}
                    </>
                  ) : null}
                  <br />
                  <a
                    href={o.mapsUrl || `https://www.google.com/maps?q=${o.lat},${o.lng}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}
